import {
  AGUARDANDO_INSPECAO,
  EM_CRIACAO,
  EM_INSPECAO,
} from '../../../constants/status';
import { tipoServicoTemSubtipo } from '../../../utils';

export const QUICK_FILTER = {
  ALL: 'all',
  ANOMALIA: 'anomalia',
  PENDENTE: 'pendente',
};

export const TIPO_SERVICO_PREFIX = 'tipo:';

export const MT_BT_SUBTIPO_ALL = 'all';

export const BASE_FILTER_OPTIONS = [
  { value: QUICK_FILTER.ALL, label: 'Todas' },
  { value: QUICK_FILTER.ANOMALIA, label: 'Anomalias' },
  { value: QUICK_FILTER.PENDENTE, label: 'Pendentes' },
];

const PENDENTE_STATUSES = [AGUARDANDO_INSPECAO, EM_INSPECAO, EM_CRIACAO];

const normalize = (value) =>
  String(value ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

export const buildTipoServicoFilterValue = (tipoServicoId) =>
  `${TIPO_SERVICO_PREFIX}${tipoServicoId}`;

export const parseTipoServicoFilterValue = (quickFilter) => {
  if (!quickFilter?.startsWith(TIPO_SERVICO_PREFIX)) return null;
  return quickFilter.slice(TIPO_SERVICO_PREFIX.length);
};

export const buildPrimaryFilterOptions = (tipoServicoOptions = []) => [
  ...BASE_FILTER_OPTIONS,
  ...tipoServicoOptions.map((option) => ({
    value: buildTipoServicoFilterValue(option.id),
    label: option.label,
  })),
];

export const getQuickFilterLabel = (quickFilter, primaryFilterOptions = []) => {
  const match = primaryFilterOptions.find((option) => option.value === quickFilter);
  return match?.label ?? null;
};

export const getActiveFilterChips = ({
  quickFilter,
  mtBtSubtipo,
  search,
  primaryFilterOptions = [],
  subtipoOptions = [],
}) => {
  const chips = [];

  const trimmedSearch = search?.trim();
  if (trimmedSearch) {
    chips.push({ key: 'search', label: `Busca: ${trimmedSearch}` });
  }

  if (quickFilter !== QUICK_FILTER.ALL) {
    const quickLabel = getQuickFilterLabel(quickFilter, primaryFilterOptions);
    if (quickLabel) chips.push({ key: 'quick', label: quickLabel });
  }

  if (mtBtSubtipo && mtBtSubtipo !== MT_BT_SUBTIPO_ALL) {
    const subtipo = subtipoOptions.find((option) => String(option.id) === String(mtBtSubtipo));
    if (subtipo) chips.push({ key: 'subtipo', label: subtipo.label });
  }

  return chips;
};

export const hasActiveFilters = (quickFilter, mtBtSubtipo, search) =>
  Boolean(search?.trim()) ||
  quickFilter !== QUICK_FILTER.ALL ||
  (mtBtSubtipo && mtBtSubtipo !== MT_BT_SUBTIPO_ALL);

export const isMtBtTipoServicoFilter = (quickFilter, mtBtTipoServicoId) =>
  parseTipoServicoFilterValue(quickFilter) === String(mtBtTipoServicoId);

export const isTipoServicoComSubtipoFilter = (quickFilter) => {
  const tipoServicoId = parseTipoServicoFilterValue(quickFilter);
  return tipoServicoId != null && tipoServicoTemSubtipo(tipoServicoId);
};

export const getNsirTipoServicoId = (nsir, tipoServicoOptions = []) => {
  if (nsir.tipoServicoId != null && nsir.tipoServicoId !== '') {
    return String(nsir.tipoServicoId);
  }

  const tipoLabel = normalize(nsir.tipoServico);
  if (!tipoLabel) return null;

  const match = tipoServicoOptions.find((option) => {
    const label = normalize(option.label);
    return label === tipoLabel || label.includes(tipoLabel) || tipoLabel.includes(label);
  });

  return match?.id ?? null;
};

export const getNsirSubtipoId = (nsir, subtipoOptions = []) => {
  if (nsir.subtipoId != null && nsir.subtipoId !== '') {
    return String(nsir.subtipoId);
  }

  const subtipoLabel = normalize(nsir.subtipo);
  if (!subtipoLabel) return null;

  const match = subtipoOptions.find((option) => normalize(option.label) === subtipoLabel);
  return match?.id ?? null;
};

export const matchesQuickFilter = (
  nsir,
  quickFilter,
  mtBtSubtipo,
  { mtBtTipoServicoId, tipoServicoOptions, subtipoOptions } = {},
) => {
  const tipoServicoId = parseTipoServicoFilterValue(quickFilter);

  if (tipoServicoId) {
    if (getNsirTipoServicoId(nsir, tipoServicoOptions) !== String(tipoServicoId)) {
      return false;
    }

    if (
      tipoServicoId &&
      mtBtSubtipo &&
      mtBtSubtipo !== MT_BT_SUBTIPO_ALL &&
      tipoServicoTemSubtipo(tipoServicoId)
    ) {
      return getNsirSubtipoId(nsir, subtipoOptions) === String(mtBtSubtipo);
    }

    return true;
  }

  switch (quickFilter) {
    case QUICK_FILTER.ANOMALIA:
      return nsir.anomalia === true;
    case QUICK_FILTER.PENDENTE:
      return PENDENTE_STATUSES.includes(nsir.status);
    default:
      return true;
  }
};

export const buildMtBtSubtipoOptions = (
  nsirs,
  { tipoServicoId, mtBtTipoServicoId, tipoServicoOptions, subtipoOptions } = {},
) => {
  const activeTipoServicoId = tipoServicoId ?? mtBtTipoServicoId;
  const filteredSubtipos = (subtipoOptions || []).filter(
    (option) => String(option.parentId) === String(activeTipoServicoId),
  );

  return filteredSubtipos.map((option) => ({
    id: option.id,
    label: option.label,
    count: nsirs.filter((nsir) => {
      if (getNsirTipoServicoId(nsir, tipoServicoOptions) !== String(activeTipoServicoId)) {
        return false;
      }
      return getNsirSubtipoId(nsir, subtipoOptions) === String(option.id);
    }).length,
  }));
};

export const TOGGLE_GROUP_SX = {
  flexWrap: 'wrap',
  gap: 0.5,
  '& .MuiToggleButton-root': {
    px: 1.25,
    py: 0.25,
    textTransform: 'none',
    fontSize: '0.75rem',
    borderRadius: '16px !important',
    border: '1px solid',
    borderColor: 'divider',
    '&.Mui-selected': {
      bgcolor: 'primary.main',
      color: 'primary.contrastText',
      '&:hover': { bgcolor: 'primary.dark' },
    },
  },
};
